import { resolveBaseUrl } from '../../lib/apiClient'
import type { ChatMessage } from '../../lib/types'

export type BalanceUpdatedEventData = {
  amount?: number
  content?: string
  transaction_id?: string
}

export type UserEventMap = {
  balance_updated: BalanceUpdatedEventData
  chat_message: ChatMessage
}

export type UserEventType = keyof UserEventMap

export type UserEventPayload<K extends UserEventType = UserEventType> = {
  type: K
  data: UserEventMap[K]
}

type AnyUserEventPayload = { type: string; data: unknown }
type AnyEventHandler = (data: unknown, payload: AnyUserEventPayload) => void

type Unsubscribe = () => void

const listeners = new Map<string, Set<AnyEventHandler>>()
const statusListeners = new Set<(connected: boolean) => void>()

let ws: WebSocket | null = null
let currentToken: string | null = null
let stopped = false
let reconnectTimer: number | null = null
let connected = false

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object'
}

function setConnected(next: boolean) {
  connected = next
  statusListeners.forEach((fn) => {
    try {
      fn(next)
    } catch {
      // ignore
    }
  })
}

function clearReconnectTimer() {
  if (reconnectTimer) {
    window.clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}

async function connectInternal(token: string) {
  clearReconnectTimer()

  try {
    const base = await resolveBaseUrl()
    const wsBase = base.replace(/^https:/i, 'wss:').replace(/^http:/i, 'ws:')
    const wsUrl = `${wsBase}/ws/chat?access_token=${encodeURIComponent(token)}`

    if (stopped) return

    const socket = new WebSocket(wsUrl)
    ws = socket

    socket.onopen = () => {
      setConnected(true)
    }

    socket.onclose = () => {
      setConnected(false)
      ws = null
      if (stopped) return
      reconnectTimer = window.setTimeout(() => {
        if (!currentToken) return
        void connectInternal(currentToken)
      }, 1500)
    }

    socket.onerror = () => {
      // onclose sẽ xử lý reconnect
    }

    socket.onmessage = (ev) => {
      try {
        const payload = JSON.parse(String(ev.data || '')) as AnyUserEventPayload
        if (!payload || typeof payload !== 'object') return
        const type = typeof (payload as any).type === 'string' ? String((payload as any).type).trim() : ''
        if (!type) return
        const data = (payload as any).data as unknown

        if ((type === 'balance_updated' || type === 'chat_message') && !isRecord(data)) return

        const set = listeners.get(type)
        if (!set || set.size === 0) return
        set.forEach((fn) => {
          try {
            fn(data, payload)
          } catch {
            // ignore
          }
        })
      } catch {
        // ignore
      }
    }
  } catch {
    setConnected(false)
    ws = null
    if (!stopped) {
      reconnectTimer = window.setTimeout(() => {
        if (!currentToken) return
        void connectInternal(currentToken)
      }, 2000)
    }
  }
}

export function ensureConnected(token: string | null | undefined) {
  const t = String(token || '').trim()
  if (!t) {
    disconnect()
    return
  }

  stopped = false

  if (ws && currentToken === t) return

  disconnect()
  currentToken = t
  void connectInternal(t)
}

export function disconnect() {
  stopped = true
  clearReconnectTimer()

  setConnected(false)
  currentToken = null

  if (ws) {
    try {
      ws.close()
    } catch {
      // ignore
    }
    ws = null
  }
}

export function isWsConnected() {
  return connected
}

export function subscribe<K extends UserEventType>(
  type: K,
  handler: (data: UserEventMap[K], payload: UserEventPayload<K>) => void
): Unsubscribe {
  const key = String(type || '').trim()
  if (!key) return () => {}

  let set = listeners.get(key)
  if (!set) {
    set = new Set<AnyEventHandler>()
    listeners.set(key, set)
  }
  set.add(handler as unknown as AnyEventHandler)

  return () => {
    const cur = listeners.get(key)
    if (!cur) return
    cur.delete(handler as unknown as AnyEventHandler)
    if (cur.size === 0) listeners.delete(key)
  }
}

export function subscribeStatus(handler: (connected: boolean) => void): Unsubscribe {
  statusListeners.add(handler)
  try {
    handler(connected)
  } catch {
    // ignore
  }
  return () => {
    statusListeners.delete(handler)
  }
}
